import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CloseModal from "../../../components/Atomos/Icons/EditMode/CloseModal";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import DropDownMultiple from "../../../components/staff/DropDownMultiple";
import Button from "../../../components/staff/Button";
import { Appstore } from "../../../redux";
import { FirmContactView } from "../../../interfaces/models/IFirmContact";
import { getJurisdictionLabel } from "../../../utils/jurisdiction/structuredJurisdiction";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import { fetchPersonByFirm } from "../../../Services/Person";
import {
  getFirmContactByFirmId,
  saveFirmContact,
} from "../../../Services/FirmContact";
import {
  contactsForFirmContactDropdown,
  firmContactBlockKey,
  serializeFirmContactPayload,
  toFirmContactPayload,
  validateFirmContactBlock,
  type ContactDropdownPerson,
} from "../../../utils/firmContactUtils";
import {
  FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS,
  isCoreFirmContactField,
  type FirmContactSlot,
} from "../../../utils/firmContactRoleConfig";
import { toReadableLabel } from "../../../utils/contactOptionLabel";
import { toastError, toastPromise } from "../../../utils/toast";
import {
  getFirmContactAllowedOfficeIds,
  formatDataAdminPersonPickerName,
  getPersonPickerOptionsForOffices,
  applyPrimaryContactFallbacks,
  mergeFirmContactsWithOffices,
  propagateParentPrimaryContactToChildJurisdictions,
  PARENT_PRIMARY_CONTACT_DEFAULT_NOTE,
  requiresCoreFirmContacts,
  normalizeOfficeServiceType,
  resolveOfficesForFirmContactProfile,
  type FirmContactProfileOfficeOptions,
  type PersonOfficeLike,
  type ProfessionalPickerOption,
} from "./manageDataAdminOfficeRules";
import NewTooltip from "../../../components/Atomos/Tooltip/NewTooltip";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

type ContactsData = FirmContactView["primaryContact"];

type ContactFieldDropdownProps = {
  slot: FirmContactSlot;
  contact: FirmContactView;
  officeId: string;
  blockKey: string;
  persons: PersonOfficeLike[];
  requireCoreContacts: boolean;
  showPrimaryDefaultNote?: boolean;
  getOfficeOptions: (
    officeId: string,
    contactType: string,
    selectedPersonId?: string,
  ) => ContactDropdownPerson[];
  updateContactBlock: (
    blockKey: string,
    updater: (current: FirmContactView) => FirmContactView,
  ) => void;
};

function ContactFieldDropdown({
  slot,
  contact,
  officeId,
  blockKey,
  persons,
  requireCoreContacts,
  showPrimaryDefaultNote = false,
  getOfficeOptions,
  updateContactBlock,
}: ContactFieldDropdownProps) {
  if (slot.cardinality === "multi") {
    const options = getOfficeOptions(officeId, slot.contactType);
    const value = contact[slot.field];

    return (
      <DropDownMultiple<ContactDropdownPerson>
        title={slot.title}
        options={options}
        value={resolveMultiDropdownValue(value, options)}
        placeholder="Select some professionals"
        renderInPortal
        onChange={(selected) => {
          updateContactBlock(blockKey, (current) =>
            applyPrimaryContactFallbacks(
              {
                ...current,
                [slot.field]: contactsFromMultiSelection(selected, persons),
              },
              { requireCoreContacts },
            ),
          );
        }}
        multiple
        maxSelections={slot.maxSelections}
      />
    );
  }

  const currentValue = contact[slot.field] as ContactsData | undefined;
  const options = getOfficeOptions(
    officeId,
    slot.contactType,
    currentValue?.id,
  );
  const isOptional = "optional" in slot && slot.optional;
  const isRequired = isCoreFirmContactField(slot.field) && requireCoreContacts;

  return (
    <div className="flex flex-col gap-1">
      <DropDownMultiple<ContactDropdownPerson>
        title={slot.title}
        required={isRequired}
        options={options}
        value={resolveSingleDropdownValue(currentValue, options)}
        placeholder="Select a professional"
        renderInPortal
        onChange={(selected) => {
          updateContactBlock(blockKey, (current) =>
            applyPrimaryContactFallbacks(
              {
                ...current,
                [slot.field]: isOptional
                  ? selected
                    ? contactFromSelection(selected, persons)
                    : undefined
                  : contactFromSelection(selected, persons),
              },
              { requireCoreContacts },
            ),
          );
        }}
      />
      {showPrimaryDefaultNote ? (
        <p className="text-sm italic text-gray-font">
          {PARENT_PRIMARY_CONTACT_DEFAULT_NOTE}
        </p>
      ) : null}
    </div>
  );
}

const EMPTY_CONTACT: ContactsData = {
  id: "",
  fullName: "",
  email: "",
  phone: { code: "", phone: "", basecode: "" },
};

function resolveSingleDropdownValue(
  contactData: ContactsData | undefined,
  options: ContactDropdownPerson[],
): ContactDropdownPerson | null {
  const id = String(contactData?.id ?? "").trim();
  if (!id) {
    return null;
  }
  return (
    options.find((option) => String(option.id) === id) ?? {
      id,
      name: toReadableLabel(contactData?.fullName) || "Unnamed professional",
    }
  );
}

function resolveMultiDropdownValue(
  contactList: ContactsData[] | undefined,
  options: ContactDropdownPerson[],
): ContactDropdownPerson[] {
  return (contactList ?? [])
    .map((item) => {
      const id = String(item.id ?? "").trim();
      if (!id) {
        return null;
      }
      return (
        options.find((option) => String(option.id) === id) ?? {
          id,
          name: toReadableLabel(item.fullName) || "Unnamed professional",
        }
      );
    })
    .filter((item): item is ContactDropdownPerson => item != null);
}

function contactFromSelection(
  selected: ContactDropdownPerson | null,
  persons: PersonOfficeLike[],
): ContactsData {
  if (!selected) {
    return { ...EMPTY_CONTACT };
  }
  const person = persons.find(
    (row) => String(row.id ?? "") === String(selected.id),
  );
  return {
    id: String(selected.id),
    fullName: selected.name,
    email: String(person?.firmEmail ?? ""),
    phone: { ...EMPTY_CONTACT.phone },
  };
}

function contactsFromMultiSelection(
  selected: ContactDropdownPerson[],
  persons: PersonOfficeLike[],
): ContactsData[] {
  return selected.map((option) => contactFromSelection(option, persons));
}

type ManageFirmContactProps = {
  firmContactData: FirmContactView[];
  offices: IOfficeNew[];
  membershipType?: string;
  includeOfficesHiddenFromDirectory?: boolean;
  onClose?: () => void;
  onSaved?: (data: FirmContactView[]) => void;
};

const ManageFirmContact = ({
  firmContactData,
  offices,
  membershipType,
  includeOfficesHiddenFromDirectory = false,
  onClose,
  onSaved,
}: ManageFirmContactProps) => {
  const manageFirm = useSelector((state: Appstore) => state.manageFirm);
  const firmId =
    manageFirm.selectedFirm?.id ?? firmContactData[0]?.firmId ?? "";
  const firmName =
    manageFirm.selectedFirm?.name ?? firmContactData[0]?.firmName ?? "";
  const { isStaffMode } = useStaffMode();

  const profileOfficeOptions = useMemo<FirmContactProfileOfficeOptions>(
    () => ({ includeOfficesHiddenFromDirectory }),
    [includeOfficesHiddenFromDirectory],
  );

  const profileOffices = useMemo(
    () => resolveOfficesForFirmContactProfile(offices, profileOfficeOptions),
    [offices, profileOfficeOptions],
  );

  const mergedInitialContacts = useMemo(
    () =>
      mergeFirmContactsWithOffices(
        firmContactData,
        offices,
        firmId,
        firmName,
        membershipType,
        profileOfficeOptions,
      ),
    [
      firmContactData,
      offices,
      firmId,
      firmName,
      membershipType,
      profileOfficeOptions,
    ],
  );

  const originalPersistedIds = useMemo(
    () =>
      new Map(
        firmContactData
          .filter((contact) => String(contact.id ?? "").trim())
          .map((contact) => [
            String(contact.officeId ?? "").trim(),
            String(contact.id).trim(),
          ]),
      ),
    [firmContactData],
  );

  const originalPayloads = useMemo(
    () =>
      new Map(
        mergedInitialContacts.map((contact: FirmContactView) => [
          String(contact.officeId ?? "").trim(),
          serializeFirmContactPayload(
            toFirmContactPayload(
              contact,
              originalPersistedIds.get(String(contact.officeId ?? "").trim()),
            ),
          ),
        ]),
      ),
    [mergedInitialContacts, originalPersistedIds],
  );

  const hydratedInitialContacts = useMemo(
    () =>
      mergedInitialContacts.map((contact) =>
        applyPrimaryContactFallbacks(contact, {
          requireCoreContacts: requiresCoreFirmContacts(
            membershipType,
            String(contact.officeId ?? "").trim(),
            profileOffices,
          ),
        }),
      ),
    [mergedInitialContacts, membershipType, profileOffices],
  );

  const [contacts, setContacts] = useState<FirmContactView[]>(
    hydratedInitialContacts,
  );
  const [persons, setPersons] = useState<PersonOfficeLike[]>([]);

  const { contacts: contactsWithDefaults, defaultedOfficeIds } = useMemo(
    () =>
      propagateParentPrimaryContactToChildJurisdictions(
        contacts,
        profileOffices,
        membershipType,
      ),
    [contacts, profileOffices, membershipType],
  );

  const defaultedOfficeIdSet = useMemo(
    () => new Set(defaultedOfficeIds),
    [defaultedOfficeIds],
  );

  useEffect(() => {
    setContacts(hydratedInitialContacts);
  }, [hydratedInitialContacts]);

  useEffect(() => {
    if (!firmId) {
      return;
    }
    void fetchPersonByFirm(firmId).then((rows) => {
      setPersons(Array.isArray(rows) ? rows : []);
    });
  }, [firmId]);

  const showJurisdictionHeading = contacts.length > 1;

  const officeServiceTypeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const office of profileOffices) {
      const id = String(office.id ?? "").trim();
      if (!id) {
        continue;
      }
      const label = normalizeOfficeServiceType(office.serviceType);
      if (label) {
        map.set(id, label);
      }
    }
    return map;
  }, [profileOffices]);

  const pickerOptionsByOffice = useMemo(() => {
    const cache = new Map<string, ContactDropdownPerson[]>();

    for (const contact of mergedInitialContacts) {
      const officeId = String(contact.officeId ?? "").trim();
      if (!officeId || cache.has(officeId)) {
        continue;
      }

      const allowedOfficeIds = getFirmContactAllowedOfficeIds(
        profileOffices,
        officeId,
      );
      cache.set(
        officeId,
        getPersonPickerOptionsForOffices(
          allowedOfficeIds,
          persons,
          profileOffices,
        ).map(
          (option: ProfessionalPickerOption) => {
            const person = persons.find(
              (row) => String(row.id ?? "") === String(option.id),
            );
            return {
              id: option.id,
              name: option.name,
              isAttorney: person?.isAttorney,
            };
          },
        ),
      );
    }
    return cache;
  }, [mergedInitialContacts, profileOffices, persons]);

  const getOfficeOptions = (
    officeId: string,
    contactType: string,
    selectedPersonId?: string,
  ): ContactDropdownPerson[] => {
    const baseOptions =
      pickerOptionsByOffice.get(String(officeId).trim()) ?? [];
    const selectedId = String(selectedPersonId ?? "").trim();
    const hasSelected = selectedId
      ? baseOptions.some((option) => String(option.id) === selectedId)
      : true;
    const selectedExtras =
      selectedId && !hasSelected
        ? persons
            .filter((row) => String(row.id ?? "").trim() === selectedId)
            .map((row) => ({
              id: selectedId,
              name:
                formatDataAdminPersonPickerName(row, profileOffices) ||
                "Unnamed professional",
              isAttorney: row.isAttorney,
            }))
        : [];
    return contactsForFirmContactDropdown(
      [...baseOptions, ...selectedExtras],
      contactType,
      selectedPersonId,
    );
  };

  const updateContactBlock = (
    blockKey: string,
    updater: (current: FirmContactView) => FirmContactView,
  ) => {
    setContacts((prev) =>
      prev.map((item) =>
        firmContactBlockKey(item) === blockKey ? updater(item) : item,
      ),
    );
  };

  const handleSave = async () => {
    const { contacts: contactsToSave } =
      propagateParentPrimaryContactToChildJurisdictions(
        contacts,
        profileOffices,
        membershipType,
      );

    for (const contact of contactsToSave) {
      const officeId = String(contact.officeId ?? "").trim();
      const validationError = validateFirmContactBlock(contact, {
        requireCoreContacts: requiresCoreFirmContacts(
          membershipType,
          officeId,
          profileOffices,
        ),
      });
      if (validationError) {
        toastError(validationError);
        return;
      }
    }

    const payloads = contactsToSave.map((contact) =>
      toFirmContactPayload(
        contact,
        originalPersistedIds.get(String(contact.officeId ?? "").trim()),
      ),
    );

    const hasChanges = payloads.some((payload) => {
      const key = String(payload.officeId ?? "").trim();
      const previous = originalPayloads.get(key);
      return serializeFirmContactPayload(payload) !== previous;
    });

    if (!hasChanges) {
      toastError("No changes to save.");
      return;
    }

    const savedViews = await toastPromise(
      (async () => {
        for (const payload of payloads) {
          const key = String(payload.officeId ?? "").trim();
          const previous = originalPayloads.get(key);
          if (serializeFirmContactPayload(payload) === previous) {
            continue;
          }
          await saveFirmContact(payload);
        }
        const refreshed = await getFirmContactByFirmId(firmId);
        return Array.isArray(refreshed) ? refreshed : [];
      })(),
      {
        pending: "Saving firm contact changes...",
        success: "Firm contacts updated successfully.",
      },
    );

    onSaved?.(savedViews as FirmContactView[]);
    onClose?.();
  };

  return (
    <div className="flex flex-col gap-6 pt-4">
      {contactsWithDefaults.map((contact) => {
        const officeId = String(contact.officeId ?? "").trim();
        const blockKey = firmContactBlockKey(contact);
        const officeServiceTypeLabel = officeServiceTypeById.get(officeId);
        const requireCoreContacts = requiresCoreFirmContacts(
          membershipType,
          officeId,
          profileOffices,
        );

        return (
          <div key={blockKey} className="flex flex-col gap-2">
            {showJurisdictionHeading ? (
              <div className="flex gap-2 items-center font-semibold text-gray-font">
                {getJurisdictionLabel({ jurisdiction: contact.jurisdiction })}
                {officeServiceTypeLabel ? ` - ${officeServiceTypeLabel}` : ""}
                <NewTooltip
                  text={
                    "Select a person from an office with the same service type as the current office."
                  }
                />
              </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS.map((slot) => (
                <ContactFieldDropdown
                  key={`${blockKey}-${slot.key}`}
                  slot={slot}
                  contact={contact}
                  officeId={officeId}
                  blockKey={blockKey}
                  persons={persons}
                  requireCoreContacts={requireCoreContacts}
                  showPrimaryDefaultNote={
                    slot.field === "primaryContact" &&
                    defaultedOfficeIdSet.has(officeId)
                  }
                  getOfficeOptions={getOfficeOptions}
                  updateContactBlock={updateContactBlock}
                />
              ))}
            </div>
          </div>
        );
      })}

      <div className="flex flex-col md:flex-row w-full justify-end gap-4 border-t border-gray-100 pt-4">
        <Button
          text="Cancel"
          color="neutral"
          variant="outlined"
          size="fixed"
          icon={<CloseModal />}
          onClick={() => onClose?.()}
        />
        <Button
          text="Update Contacts"
          color={isStaffMode ? "secondary" : "primary"}
          variant="filled"
          size="fixed"
          icon={<SaveChanges />}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default ManageFirmContact;
